import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, MapPin, BookOpen } from 'lucide-react';
import { useCompetition } from '@/hooks/useApi';

export default function CompetitionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { data: competition, isLoading, error } = useCompetition(id || '');

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 animate-pulse space-y-4">
        <div className="h-6 bg-bg-secondary rounded w-1/4" />
        <div className="h-10 bg-bg-secondary rounded w-3/4" />
        <div className="h-40 bg-bg-secondary rounded" />
      </div>
    );
  }

  if (error || !competition) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-text-secondary mb-4">{t('common.error')}</p>
        <Link to="/competitions" className="btn-primary">{t('common.backToList')}</Link>
      </div>
    );
  }

  const genderMap: Record<string, string> = {
    male: t('competitions.male'),
    female: t('competitions.female'),
    mixed: t('competitions.mixed'),
    open: t('competitions.open'),
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link
        to="/competitions"
        className="inline-flex items-center gap-1 text-primary text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <ArrowLeft className="w-4 h-4" /> {t('common.backToList')}
      </Link>

      <h1 className="text-3xl md:text-4xl font-bold mb-2">
        {i18n.language === 'en' ? competition.nameEn : competition.name}
      </h1>

      <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary mt-4 mb-8">
        <span className="badge">{genderMap[competition.gender]}</span>
        <span className="flex items-center gap-1">
          <Users className="w-4 h-4" />
          {t('competitions.ageBracket')}: {competition.ageBracket}
        </span>
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          {competition.venue}
        </span>
      </div>

      <div className="card mb-8">
        <p className="text-text-secondary leading-relaxed">{competition.description}</p>
      </div>

      <div className="card mb-8">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          {t('competitions.rules')}
        </h2>
        <div className="text-text-secondary leading-relaxed whitespace-pre-line">{competition.rules}</div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card text-center">
          <div className="text-3xl font-bold text-primary">{competition.registeredTeams}</div>
          <div className="text-sm text-text-secondary">{t('competitions.registeredTeams')}</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-primary">{competition.participants}</div>
          <div className="text-sm text-text-secondary">{t('competitions.participants')}</div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <Link to={`/scores/${competition.id}`} className="btn-primary">
          {t('nav.liveScores')}
        </Link>
      </div>
    </div>
  );
}
